import { useState } from "react";
import { Copy, Plus, Trash2 } from "lucide-react";
import { PRODUCT_COLOR_KEY, rulesNoteEn, type OptionRule } from "@erc/shared";

export interface RuleField { key: string; label: string; labelEn: string; values: string[] }

// Secenek kurallari: "EGER <alan> su degerlerden biriyse, <diger alan> su
// degerleri ALAMAZ". Kolaj planindaki satirlar bu kurallarla denetlenir;
// alici notu Etsy kisisellestirme talimatina kopyalanir.
export function OptionRulesEditor({ fields, rules, onChange }: {
  fields: RuleField[];
  rules: OptionRule[];
  onChange: (rules: OptionRule[]) => void;
}) {
  const [ifKey, setIfKey] = useState(fields[0]?.key ?? "");
  const [thenKey, setThenKey] = useState(fields[1]?.key ?? fields[0]?.key ?? "");
  const [ifValues, setIfValues] = useState<string[]>([]);
  const [notValues, setNotValues] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const f = (k: string) => fields.find((x) => x.key === k);
  const toggle = (arr: string[], v: string) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const notes = rulesNoteEn(rules, Object.fromEntries(fields.map((x) => [x.key, x.labelEn])));

  const add = () => {
    if (!ifKey || !thenKey || ifKey === thenKey || !ifValues.length || !notValues.length) return;
    onChange([...rules, { id: `r${Date.now().toString(36)}`, ifKey, ifValues, thenKey, notValues }]);
    setIfValues([]); setNotValues([]);
  };

  const chips = (k: string, sel: string[], set: (v: string[]) => void) => (
    <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto">
      {(f(k)?.values ?? []).map((v) => (
        <button key={v} onClick={() => set(toggle(sel, v))}
          className={`rounded-lg border px-1.5 py-0.5 ${sel.includes(v) ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          {v}
        </button>
      ))}
    </div>
  );

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-gray-200 p-2 text-xs">
      <p className="font-medium text-gray-800">Seçenek kuralları</p>
      <p className="text-gray-500">Olmayacak kombinasyonları yaz (ör. ürün rengi White iken yazı rengi White olamaz). Plandaki satırlar bu kurallarla ve renk kontrastıyla denetlenir.</p>
      {rules.map((r) => (
        <div key={r.id} className="flex items-start gap-2 rounded-lg bg-gray-50 px-2 py-1">
          <span className="flex-1 text-gray-700">
            EĞER <b>{f(r.ifKey)?.label ?? r.ifKey}</b> = {r.ifValues.join(" / ")} → <b>{f(r.thenKey)?.label ?? r.thenKey}</b> olamaz: {r.notValues.join(" / ")}
          </span>
          <button onClick={() => onChange(rules.filter((x) => x.id !== r.id))} className="text-gray-400 hover:text-red-600" title="Kuralı sil">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      {fields.length >= 2 && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="flex items-center gap-1 text-gray-600">EĞER
              <select value={ifKey} onChange={(e) => { setIfKey(e.target.value); setIfValues([]); }} className="rounded-lg border border-gray-200 px-1 py-0.5">
                {fields.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select> şunlardan biriyse
            </label>
            {chips(ifKey, ifValues, setIfValues)}
          </div>
          <div className="space-y-1">
            <label className="flex items-center gap-1 text-gray-600">
              <select value={thenKey} onChange={(e) => { setThenKey(e.target.value); setNotValues([]); }} className="rounded-lg border border-gray-200 px-1 py-0.5">
                {fields.filter((x) => x.key !== ifKey).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select> şunları alamaz
            </label>
            {chips(thenKey, notValues, setNotValues)}
          </div>
          <button onClick={add} disabled={!ifValues.length || !notValues.length || ifKey === thenKey}
            className="flex w-fit items-center gap-1 rounded-lg border border-indigo-300 px-2 py-0.5 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40">
            <Plus className="h-3 w-3" /> Kural ekle
          </button>
        </div>
      )}
      {notes.length > 0 && (
        <div className="rounded-lg bg-sky-50 p-2 text-sky-900">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="font-medium">Alıcı notu (Etsy kişiselleştirme talimatına ekle)</span>
            <button onClick={() => { void navigator.clipboard?.writeText(notes.join("\n")).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }); }}
              className="flex items-center gap-0.5 text-sky-700 hover:underline"><Copy className="h-3 w-3" />{copied ? "kopyalandı" : "kopyala"}</button>
          </div>
          {notes.map((n, i) => <p key={i}>{n}</p>)}
        </div>
      )}
    </div>
  );
}

export { PRODUCT_COLOR_KEY };
